import { Box, Card, CardContent, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import type React from "react";
import { useApi, useApiQuery } from "../hooks";
import type { DataAsset } from "../types";
import { DataAssetsTable } from "./data-assets-table";

interface TestDataAssetsTabProps {
  testId: string;
}

export const TestDataAssetsTab: React.FC<TestDataAssetsTabProps> = ({ testId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { callApi } = useApi<DataAsset>();

  const { data: dataAssets, isLoading } = useApiQuery<DataAsset[]>(`/tests/${testId}/data-assets`);

  const { mutate: fetchAssetById } = useMutation({
    mutationFn: async (id: string) => {
      return callApi(`/data-assets/${id}`, {
        method: "GET",
      });
    },
    onSuccess: (data: DataAsset) => {
      if (data.file_path) {
        window.open(data.file_path, "_blank");
      } else if (data.raw_data) {
        const blob = new Blob([data.raw_data], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else {
        enqueueSnackbar("No content available for this asset", {
          variant: "warning",
        });
      }
    },
    onError: () => {
      enqueueSnackbar("Failed to fetch data asset", { variant: "error" });
    },
  });

  const handleView = (asset: DataAsset) => {
    fetchAssetById(asset.id);
  };

  if (!dataAssets || dataAssets.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Data Assets
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This test doesn't have any associated data assets.
              <br />
              Data assets can be configured in the application settings.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Associated Data Assets
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Data assets that are available for this test. These can be used as test data during
            execution.
          </Typography>
        </Box>

        <DataAssetsTable
          data={dataAssets}
          isLoading={isLoading}
          onView={handleView}
          enableEdit={false}
          enableDelete={false}
          enableView={true}
          height={400}
        />
      </CardContent>
    </Card>
  );
};
